import { DateTime } from '@ksyos/date-time';
import { Alert } from '@ksyos/design-system';
import classnames from 'classnames';
import { useEffect, useState } from 'react';
import Async from 'react-select/async';
import { getSelectedLanguage } from '@frontend/translate';
import type { IReactSelectOptions } from '../interfaces/reactSelectOptions';
import type {
    IServiceProviderOrganizationsPractitioner,
    IServiceProviderOrganizationsPractitioners,
} from '../interfaces/serviceProviderOrganizationPractitioners';
import { PractitionerRoleInRequest } from '../../../common/enums/practitionerRoleInRequest';
import { ServiceCode } from '../../../common/enums/serviceCode';
import { ServiceType } from '../../../common/enums/serviceType';
import { getDisplayName } from '../../../common/utils/getDisplayName';
import { parseNameForSearch } from '../../../common/utils/parseNameForSearch';
import { fetchJson } from '../../store/fetch';
import { ReadOnlyInputText } from './common';
import { FormValidationMessage } from './FormValidationMessage';
import { SelectOption } from './SelectOption';
import { ServiceOrganizationProviderSelectionSearch } from './ServiceOrganizationProviderSearch';

interface IServiceOrganizationProviderSelectionProps {
    addOctPracticesInformation?: boolean;
    error?: string;
    excludeNonOctPractices?: boolean;
    initialOrganizationId: string | null;
    initialPractitionerId: string | null;
    isExaminationRequest?: boolean;
    isExternalHolter?: boolean;
    isReadOnlyIfSingleServiceOrganizationProvider?: boolean;
    onChange: (organizationId: string | null, practitionerId: string | null) => Promise<void>;
    onDoValidate?: () => void;
    roleOfPractitionersInList?: PractitionerRoleInRequest;
    serviceCode?: ServiceCode;
    serviceProviderServiceId: string;
    serviceType?: ServiceType;
}

export interface IOrganizationPractitionerOptions {
    applicationWaitingTime?: number;
    disabled: boolean;
    label: string;
    practitioners: {
        disabled: boolean;
        label: string;
        value: string;
    }[];
    treatmentWaitingTime?: number;
    value: string;
}

export function ServiceOrganizationProviderSelection(props: IServiceOrganizationProviderSelectionProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [organizationPractitioners, setOrganizationPractitioners] = useState<IOrganizationPractitionerOptions[]>([]);
    const [selectedOptionLabel, setSelectedOptionLabel] = useState<string | null>(null);
    const [providersAreAvailable, setProvidersAreAvailable] = useState(true);
    const [total, setTotal] = useState<number>();
    const [selectKey, setSelectKey] = useState('');
    const [initialOrganizationIdAndPractitionerId, setInitialOrganizationIdAndPractitionerId] = useState('');
    const translate = getSelectedLanguage();

    const {
        initialPractitionerId,
        initialOrganizationId,
        roleOfPractitionersInList,
        serviceProviderServiceId,
        serviceType,
        serviceCode,
        error,
        isExaminationRequest,
        onChange,
        onDoValidate,
        excludeNonOctPractices,
        addOctPracticesInformation,
        isReadOnlyIfSingleServiceOrganizationProvider,
    } = props;

    useEffect(() => {
        let key = 'select';
        if (initialPractitionerId) {
            key += `-${initialPractitionerId}`;
        }
        if (addOctPracticesInformation) {
            key += '-extra-info';
        }

        if (excludeNonOctPractices) {
            key += '-excluded';
        }

        setSelectKey(key);
    }, [addOctPracticesInformation, excludeNonOctPractices, initialPractitionerId]);

    useEffect(() => {
        if (organizationPractitioners.length === 0) {
            setProvidersAreAvailable(false);
        } else {
            setProvidersAreAvailable(true);
        }

        if (organizationPractitioners.length === 1) {
            const organization = organizationPractitioners[0];

            if (
                organization.practitioners.length === 0 ||
                organization.practitioners.every((practitioner) => practitioner.disabled)
            ) {
                handleChange(organization.value, organization.label);
            } else if (organization.practitioners.length === 1) {
                onChangeHandler(organization.practitioners[0].value);
            }
        }
    }, [organizationPractitioners]);

    useEffect(() => {
        setSelectedOptionLabel(null);
        loadOrganizations(serviceProviderServiceId);
    }, [serviceProviderServiceId]);

    useEffect(() => {
        if (selectedOptionLabel !== null) {
            onDoValidate?.();
        }
    }, [selectedOptionLabel]);

    useEffect(() => {
        setInitialOrganizationIdAndPractitionerId(`${initialOrganizationId}:${initialPractitionerId}`);
    }, [props]);

    useEffect(() => {
        loadInitialOrganizationPractitioner(serviceProviderServiceId);
    }, [initialOrganizationIdAndPractitionerId]);

    if (isLoading) {
        return null;
    }

    const readOnlyLabel = getReadOnlyLabel();
    if (readOnlyLabel) {
        return <ReadOnlyInputText value={readOnlyLabel} />;
    }

    if (
        (serviceType !== ServiceType.referral && serviceType !== ServiceType.referralSmall) ||
        serviceCode === ServiceCode.OSASV
    ) {
        return (
            <>
                <Async<IReactSelectOptions>
                    className={classnames(
                        'select-form-control',
                        'automation-sendTo',
                        selectedOptionLabel ? 'selected-option' : ''
                    )}
                    classNamePrefix="Select"
                    components={{ Option: SelectOption }}
                    defaultOptions
                    key={`${selectKey}-reload`}
                    loadOptions={(input: string) => getOptions(input)}
                    noOptionsMessage={() => translate.common.typeToSearch}
                    onChange={(selectedOption) => {
                        if (selectedOption && !Array.isArray(selectedOption)) {
                            handleChange(selectedOption.value, selectedOption.label);
                        }
                    }}
                    placeholder={selectedOptionLabel || translate.request.choosePractitionerOrOrganizationOrCity}
                />
                <FormValidationMessage error={error} />
            </>
        );
    }

    if (providersAreAvailable) {
        return (
            <ServiceOrganizationProviderSelectionSearch
                clearSelectedItem={async () => await onChange(null, null)}
                error={error}
                initialOrganizationId={initialOrganizationId}
                initialValue={selectedOptionLabel || undefined}
                onChange={async (selectedOption) => {
                    handleChange(selectedOption.value, selectedOption.label);
                }}
                onSearch={(input?: string, page?: number) => loadOrganizations(serviceProviderServiceId, input, page)}
                organizations={organizationPractitioners}
                placeholder={translate.common.searchNameOrCity}
                total={total || 0}
            />
        );
    } else {
        //  Render the error message last to prevent flash of error message in a re-render when providers are available
        return <Alert appearance="warning">{translate.request.noProvidersForRequest}</Alert>;
    }

    function handleChange(value?: string, label?: string) {
        onChangeHandler(value || null);
        setSelectedOptionLabel(label || null);
    }

    function onChangeHandler(organizationPractitionerIds: string | null) {
        if (organizationPractitionerIds) {
            if (organizationPractitionerIds.includes('_')) {
                onChange(organizationPractitionerIds.split('_')[0], organizationPractitionerIds.split('_')[1]);
            } else {
                onChange(organizationPractitionerIds, null);
            }
        } else {
            onChange(null, null);
        }
    }

    async function loadOrganizationPractitionerOptions(
        serviceId: string,
        searchInput?: string,
        page?: number
    ): Promise<IOrganizationPractitionerOptions[]> {
        const isBiometrician =
            roleOfPractitionersInList === PractitionerRoleInRequest.biometrician ? '&isBiometrician=true' : '';
        const searchText = searchInput ? `&searchText=${encodeURIComponent(parseNameForSearch(searchInput))}` : '';
        const isReferral = serviceType === ServiceType.referral ? '&isReferral=true' : '';
        const isReferralSmall = serviceType === ServiceType.referralSmall ? '&isReferralSmall=true' : '';
        const excludeNonOctPractice = excludeNonOctPractices ? '&excludeNonOctPractices=true' : '';
        const sortOnOctPractice = addOctPracticesInformation ? '&sortOnOctPractice=true' : '';
        const isExternalHolter = props.isExternalHolter ? '&isExternalHolter=true' : '';
        const pageNumber = page ? page : 1;
        const limit =
            (serviceType !== ServiceType.referral && serviceType !== ServiceType.referralSmall) ||
            serviceCode === ServiceCode.OSASV
                ? 50
                : 10;
        const paging = `&page=${pageNumber}&limit=${limit}`;
        const url = `/api/services/${serviceId}/serviceProviders/organizationsAndPractitioners?${isBiometrician}${searchText}${isExternalHolter}${isReferral}${isReferralSmall}${paging}${excludeNonOctPractice}${sortOnOctPractice}`;

        const result = await fetchJson(url, 'GET');
        if (result.json.total !== null && result.json.total !== undefined) {
            setTotal(result.json.total);
        }
        const organizationPractitionersResult: IOrganizationPractitionerOptions[] = [];
        for (const organization of result.json.items as IServiceProviderOrganizationsPractitioners[]) {
            const practitionerOptions: { disabled: boolean; isAvailable: boolean; label: string; value: string }[] = [];
            let hasHiddenPractitioners = false;
            for (const practitioner of organization.practitioners) {
                // Selection should be enabled when:
                // The practitioner is NOT absent
                // AND
                //          We are selecting a grader
                //      OR
                //          We are selecting a non grader that has request recipient enabled in their settings
                const isAvailable = practitioner.absenceEndDate === null && practitioner.isActivated;
                const isSelectable = isAvailable && practitioner.isRequestRecipient;

                if (organization.isOrganizationRequestRecipient && isExaminationRequest && !isSelectable) {
                    hasHiddenPractitioners = true;
                    continue;
                }

                practitionerOptions.push({
                    disabled: !isSelectable,
                    isAvailable: isAvailable,
                    label: createPractitionerLabel(practitioner),
                    value: `${organization.id}_${practitioner.id}`,
                });
            }

            const hasAvailablePractitioners =
                practitionerOptions.some((practitioner) => practitioner.isAvailable) || hasHiddenPractitioners;

            organizationPractitionersResult.push({
                applicationWaitingTime: organization.applicationWaitingTime,
                disabled:
                    serviceType !== ServiceType.referral &&
                    serviceType !== ServiceType.referralSmall &&
                    (!organization.isOrganizationRequestRecipient || !hasAvailablePractitioners),
                label: createOrganizationLabel(organization),
                practitioners: practitionerOptions,
                treatmentWaitingTime: organization.treatmentWaitingTime,
                value: organization.id,
            });
        }

        return [...organizationPractitionersResult];
    }

    async function loadOrganizations(serviceId: string, searchInput?: string, page?: number) {
        setOrganizationPractitioners(await loadOrganizationPractitionerOptions(serviceId, searchInput, page));
    }

    async function getOptions(input: string) {
        const updatedOrganizationPractitioners = await loadOrganizationPractitionerOptions(
            serviceProviderServiceId,
            input
        );
        const options: { className?: string; isDisabled: boolean; label: string; value: string }[] = [];
        updatedOrganizationPractitioners.forEach((organization) => {
            const className = organization.disabled ? 'Select__group-header is-disabled' : 'Select__group-header';
            options.push({
                className: className,
                isDisabled: organization.disabled,
                label: organization.label,
                value: organization.value,
            });
            for (const practitioner of organization.practitioners) {
                const nameClass = practitioner.disabled ? 'Select__group-option is-disabled' : 'Select__group-option';
                options.push({
                    className: nameClass,
                    isDisabled: practitioner.disabled,
                    label: practitioner.label,
                    value: practitioner.value,
                });
            }
        });

        return options;
    }

    async function loadInitialOrganizationPractitioner(serviceId: string) {
        setIsLoading(true);
        let createdSelectedOptionLabel = null;
        if (initialOrganizationId) {
            let url = `/api/organizations/${initialOrganizationId}`;
            const organizationResult = await fetchJson(url, 'GET');
            createdSelectedOptionLabel = organizationResult.json
                ? createOrganizationLabel(organizationResult.json)
                : null;
            if (initialPractitionerId) {
                url = `/api/practitioners/${initialPractitionerId}`;
                const practitionerResult = await fetchJson(url, 'GET');

                url = `/api/serviceProviders?serviceId=${serviceId}&organizationId=${initialOrganizationId}&practitionerId=${initialPractitionerId}&isActive=true`;
                const serviceProviderResult = await fetchJson(url, 'GET');

                if (serviceProviderResult.json.total === 1) {
                    const practitioner = {
                        ...practitionerResult.json,
                        isRequestRecipient: serviceProviderResult.json.items[0].isRequestRecipient,
                    };
                    createdSelectedOptionLabel = practitioner ? createPractitionerLabel(practitioner) : null;
                }
            }
        }
        setSelectedOptionLabel(createdSelectedOptionLabel);
        setIsLoading(false);
    }

    function createPractitionerLabel(practitioner: IServiceProviderOrganizationsPractitioner) {
        let label = getDisplayName(practitioner);
        if (practitioner.absenceEndDate) {
            label += ` (${translate.request.absentUpToAndIncluding} ${DateTime.from(practitioner.absenceEndDate).format(
                'DD-MM-YYYY'
            )})`;
        } else if (!practitioner.isRequestRecipient) {
            label += ` (${translate.request.selectableOnOrganization})`;
        }

        return label;
    }

    function createOrganizationLabel(organization: IServiceProviderOrganizationsPractitioners) {
        if (
            serviceType !== ServiceType.referralSmall &&
            serviceType !== ServiceType.referral &&
            serviceCode === ServiceCode.OSASV
        ) {
            return `${organization.name} ${organization.city ? `(${organization.city})` : ''}`;
        } else if (addOctPracticesInformation) {
            return `${organization.name}${organization.canPerformGlaucoomExamination ? ' (OCT-scan aanwezig)' : ''} ${
                organization.city ? `, ${organization.city}` : ''
            }`;
        } else {
            return `${organization.name}${organization.city ? `, ${organization.city}` : ''}`;
        }
    }

    function getReadOnlyLabel() {
        if (!initialOrganizationId || organizationPractitioners.length !== 1) {
            return;
        }

        const organization = organizationPractitioners[0];
        let readOnlyLabelToCreate;

        if (serviceCode === ServiceCode.OSASV) {
            if (
                organization.practitioners.length === 0 ||
                (organization.practitioners.length === 1 && organization.practitioners[0].disabled)
            ) {
                readOnlyLabelToCreate = organization.label;
            } else if (organization.practitioners.length === 1) {
                readOnlyLabelToCreate = organization.practitioners[0].label;
            }
        } else if (
            isReadOnlyIfSingleServiceOrganizationProvider &&
            organization.practitioners.every((practitioner) => practitioner.disabled)
        ) {
            readOnlyLabelToCreate = organization.label;
        }

        return readOnlyLabelToCreate;
    }
}
